import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import MagicButton from 'components/MagicButton/MagicButton';
import { AssetUrls } from 'config/assets';
import { sanitizeText } from 'utils/inputSanitization';
import './FeedbackModal.css';

export type FeedbackType = 'bug_report' | 'feature_request' | 'general';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: FeedbackData) => void;
  isLoading?: boolean;
}

export interface FeedbackData {
  feedbackType: FeedbackType;
  // Bug Report fields
  overview?: string;
  currentBehavior?: string;
  desiredBehavior?: string;
  // Feature Request fields
  featureOverview?: string;
  coreProblem?: string;
  proposedSolution?: string;
  // General Feedback fields
  sentiment?: 'frustrated' | 'neutral' | 'happy';
  generalFeedback?: string;
  responsePreference?: 'yes' | 'no' | 'no_preference';
}

const FeedbackModal: React.FC<Props> = ({ isOpen, onClose, onSubmit, isLoading = false }) => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('bug_report');
  const [formData, setFormData] = useState<FeedbackData>({
    feedbackType: 'bug_report',
    overview: '',
    currentBehavior: '',
    desiredBehavior: '',
    featureOverview: '',
    coreProblem: '',
    proposedSolution: '',
    sentiment: 'neutral',
    generalFeedback: '',
    responsePreference: 'no_preference'
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset to initial state when modal is closed
      setFeedbackType('bug_report');
      setFormData({
        feedbackType: 'bug_report',
        overview: '',
        currentBehavior: '',
        desiredBehavior: '',
        featureOverview: '',
        coreProblem: '',
        proposedSolution: '',
        sentiment: 'neutral',
        generalFeedback: '',
        responsePreference: 'no_preference'
      });
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const dataToSubmit: FeedbackData = {
      ...formData,
      feedbackType
    };
    onSubmit(dataToSubmit);
  };

  const handleTypeChange = (type: FeedbackType) => {
    setFeedbackType(type);
    setFormData(prev => ({ ...prev, feedbackType: type }));
  };

  const updateField = (field: keyof FeedbackData, value: string) => {
    // Sanitize input based on field type
    const sanitizedValue = field === 'responsePreference' || field === 'sentiment' 
      ? value // These are select fields with controlled values
      : sanitizeText(value); // Sanitize all text inputs
    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2 className="feedback-modal-title">Send feedback</h2>
          <button className="feedback-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="feedback-modal-content">
          {/* Feedback Type Selector */}
          <div className="feedback-form-group">
            <label className="feedback-label">Feedback type</label>
            <select 
              className="feedback-select"
              value={feedbackType}
              onChange={(e) => handleTypeChange(e.target.value as FeedbackType)}
            >
              <option value="bug_report">Bug Report</option>
              <option value="feature_request">Feature Request</option>
              <option value="general">General Feedback</option>
            </select>
          </div>

          {/* Bug Report Fields */}
          {feedbackType === 'bug_report' && (
            <>
              <div className="feedback-form-group">
                <label className="feedback-label">Overview of the issue:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the issue in a sentence or two."
                  value={formData.overview}
                  onChange={(e) => updateField('overview', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Current behavior</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the issue in a sentence or two."
                  value={formData.currentBehavior}
                  onChange={(e) => updateField('currentBehavior', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Desired behavior:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the issue in a sentence or two."
                  value={formData.desiredBehavior}
                  onChange={(e) => updateField('desiredBehavior', e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Feature Request Fields */}
          {feedbackType === 'feature_request' && (
            <>
              <div className="feedback-form-group">
                <label className="feedback-label">Overview of feature</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Summarize the feature in a sentence or two."
                  value={formData.featureOverview}
                  onChange={(e) => updateField('featureOverview', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Core problem:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="What is the underlying issue or struggle you're facing?"
                  value={formData.coreProblem}
                  onChange={(e) => updateField('coreProblem', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Proposed solution:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Describe your solution to the core problem"
                  value={formData.proposedSolution}
                  onChange={(e) => updateField('proposedSolution', e.target.value)}
                  rows={3}
                />
              </div>
            </>
          )}

          {/* General Feedback Fields */}
          {feedbackType === 'general' && (
            <>
              <div className="feedback-form-group">
                <label className="feedback-label">How are you feeling?</label>
                <div className="feedback-sentiment-buttons">
                  <button
                    className={`feedback-sentiment-btn ${formData.sentiment === 'frustrated' ? 'selected' : ''}`}
                    onClick={() => updateField('sentiment', 'frustrated')}
                  >
                    😤
                  </button>
                  <button
                    className={`feedback-sentiment-btn ${formData.sentiment === 'neutral' ? 'selected' : ''}`}
                    onClick={() => updateField('sentiment', 'neutral')}
                  >
                    😐
                  </button>
                  <button
                    className={`feedback-sentiment-btn ${formData.sentiment === 'happy' ? 'selected' : ''}`}
                    onClick={() => updateField('sentiment', 'happy')}
                  >
                    😊
                  </button>
                </div>
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Feedback:</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="What's on your mind?"
                  value={formData.generalFeedback}
                  onChange={(e) => updateField('generalFeedback', e.target.value)}
                  rows={5}
                />
              </div>

              <div className="feedback-form-group">
                <label className="feedback-label">Would you like a response to this feedback?</label>
                <select 
                  className="feedback-select"
                  value={formData.responsePreference}
                  onChange={(e) => updateField('responsePreference', e.target.value as 'yes' | 'no' | 'no_preference')}
                >
                  <option value="no_preference">No preference</option>
                  <option value="yes">Yes, please respond</option>
                  <option value="no">No response needed</option>
                </select>
              </div>
            </>
          )}
        </div>

        <div className="feedback-modal-footer">
          <MagicButton
            label={isLoading ? "Sending..." : "Send Feedback"}
            color="purple"
            onPress={handleSubmit}
            isDisabled={isLoading}
            isLoading={isLoading}
            fullWidth
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default FeedbackModal;